import { inject, Injectable } from '@angular/core';
import { combineLatest, map, Observable } from 'rxjs';
import { selectAllLoggerMeasurements } from '@app/main/main-screen/store/data/logger-measurements/logger-measurements.selectors';
import { Store } from '@ngrx/store';
import {
  selectAllLoggerDeployments,
  selectLoggerDeploymentEntities,
} from '@app/main/main-screen/store/data/logger-deployments/logger-deployments.selectors';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { shareReplay } from 'rxjs/operators';
import { ILoggerDeployment } from '@app/api/interfaces/logger-deployment-data.interface';
import { ILoggerMeasurement } from '@app/api/interfaces/logger-measurement.interface';

@Injectable({ providedIn: 'root' })
export class LdDataAggregationService {
  private readonly store = inject(Store);

  public readonly ldLoggers$: Observable<ILdLogger[]> = combineLatest([
    this.store.select(selectAllLoggerMeasurements),
    this.store.select(selectAllLoggerDeployments),
    this.store.select(selectLoggerDeploymentEntities),
  ]).pipe(
    map(([loggerMeasurements, loggerDeployments, loggerDeploymentsMap]) => {
      if (!loggerDeployments.length) return [];

      const usedDeploymentIds = new Set<string>();

      const ldLoggers: ILdLogger[] = loggerMeasurements
        .filter(loggerMeasurement => loggerDeploymentsMap[loggerMeasurement.deploymentId])
        .map(loggerMeasurement => {
          usedDeploymentIds.add(loggerMeasurement.deploymentId);

          const deployment = loggerDeploymentsMap[loggerMeasurement.deploymentId]!;

          return this.prepareLdLogger(deployment, loggerMeasurement);
        });

      return [
        ...ldLoggers,
        ...loggerDeployments
          .filter(deployment => !usedDeploymentIds.has(deployment.id))
          .map(deployment => this.prepareLdLogger(deployment)),
      ];
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public prepareLdLogger(deployment: ILoggerDeployment, measurement?: ILoggerMeasurement): ILdLogger {
    return {
      id: measurement ? `${deployment.id}_${measurement.id}` : `${deployment.id}_undefined`,
      measurement,
      deployment,
    };
  }
}
